import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Box,
  ButtonBase,
  type InputBaseComponentProps,
  TextField as MuiTextField,
  Tooltip,
} from "@mui/material";
import { useTranslate, useTolgee } from "@tolgee/react";
import { useField } from "formik";
import React from "react";
import { isSupportedCountry } from "react-phone-number-input";
import PhoneInput from "react-phone-number-input/input";
import {
  OnboardingCountryCodeMenu,
  OnboardingFlag,
  useOnboardingPhoneCountries,
} from "./OnboardingCountryCodeMenu.js";
import {
  DEFAULT_PHONE_COUNTRY,
  getCountryOfNumber,
  getDialCode,
  getInitialPhoneCountry,
  isInternationalEntry,
  parseInternationalEntry,
  getPhoneInputValue,
  switchPhoneCountry,
  type PhoneCountry,
} from "./phoneCountry.js";
import { FIELD_VALUE_TEXT_SX } from "./onboardingFormStyles.js";

/** Width of the country-code segment; the label starts right after it (design `.phone`). */
const SEGMENT_WIDTH = 98;
/** Where the label starts (the board's `left: 106px`). */
const LABEL_LEFT = 106;
// Where the number's value line starts: the theme's filled input with a label
// pads its input 21px from the top and makes it 1.4375em high. The dial code
// is drawn in the same box, so both texts share one baseline.
const VALUE_TOP = 21;
const VALUE_LINE_HEIGHT = "1.4375em";
const DIVIDER_COLOR = "#CBD2E0";
// The lifted labels' colours, as the other fields have them
// (`ONBOARDING_FIELDS_SX`).
const LABEL_COLOR = "#1A4CBF";
const LABEL_FOCUSED_COLOR = "#001E64";
const LABEL_ERROR_COLOR = "#AF0900";

// `src/app/overrides.d.ts` types `React.forwardRef` as returning a plain
// function, so generics infer; react-phone-number-input's `inputComponent`
// needs what it returns at runtime, a forward-ref component.
const forwardRefComponent = React.forwardRef as <T, P>(
  render: React.ForwardRefRenderFunction<T, P>
) => React.ForwardRefExoticComponent<
  React.PropsWithoutRef<P> & React.RefAttributes<T>
>;

/**
 * The `<input>` of the national-number input. A number that starts with `+` or
 * `00` is not a national one: it is handed to `onInternationalEntry` instead of
 * the library, which would read "+49 30…" in Germany as "+4949 30…".
 */
const InternationalAwareInput = forwardRefComponent<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & {
    draft?: string | null;
    onInternationalEntry?: (raw: string | null) => void;
  }
>(function InternationalAwareInput(
  { draft, onInternationalEntry, onChange, value, ...other },
  ref
) {
  return (
    <input
      {...other}
      ref={ref}
      value={draft ?? value}
      onChange={event => {
        if (onInternationalEntry && isInternationalEntry(event.target.value)) {
          onInternationalEntry(event.target.value);
          return;
        }
        onInternationalEntry?.(null);
        onChange?.(event);
      }}
    />
  );
});

/**
 * The input element MUI renders: react-phone-number-input's national-number
 * input for the chosen country. It reports the E.164 number to MUI as a change
 * event, the way MUI inputs do.
 */
const NationalNumberInput = React.forwardRef<
  HTMLInputElement,
  InputBaseComponentProps
>(function NationalNumberInput(
  { country, onChange, name, draft, onInternationalEntry, ...other },
  ref
) {
  return (
    <PhoneInput
      {...other}
      ref={ref}
      name={name}
      inputComponent={InternationalAwareInput}
      draft={typeof draft === "string" ? draft : null}
      onInternationalEntry={onInternationalEntry}
      // MUI hands the `htmlInput` props over untyped.
      country={
        typeof country === "string" && isSupportedCountry(country)
          ? country
          : DEFAULT_PHONE_COUNTRY
      }
      // The calling code is the segment next to the field; shown this way
      // the number has no national trunk prefix ("30 1234567", not
      // "030 1234567"), while the value stays E.164.
      international
      withCountryCallingCode={false}
      onChange={value =>
        onChange?.({
          target: { name, value: value ?? "" },
        } as React.ChangeEvent<HTMLInputElement>)
      }
    />
  );
});

interface Props {
  /** The Formik path of the phone number, kept as an E.164 number. */
  name: string;
  label: string;
  disabled?: boolean;
}

/**
 * A phone field with a country-code button in its filled field: flag, calling
 * code and chevron, then a divider, then the national number. The form value
 * stays an E.164 number (`+49…`); the country is the one of a stored number,
 * else that of the UI language, else Germany. Changing the country keeps the
 * national number.
 */
export const OnboardingPhoneField = ({ name, label, disabled }: Props) => {
  const { t } = useTranslate(["OrganisationProfile"]);
  const language = useTolgee(["language"]).getLanguage();
  const [{ value }, { touched, error }, helpers] = useField<string>(name);
  const phone = value ?? "";
  const [country, setCountry] = React.useState<PhoneCountry>(() =>
    getInitialPhoneCountry({ storedNumber: phone, locale: language })
  );
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [focused, setFocused] = React.useState(false);
  // An international number still being typed (`+4`), shown as typed.
  const [draft, setDraft] = React.useState<string | null>(null);
  const countries = useOnboardingPhoneCountries();
  const countryName =
    countries.find(option => option.code === country)?.name ?? country;

  // A number the form receives from elsewhere (a website lookup, Undo, a
  // restored suggestion) carries its own country. A picked country that shares
  // the number's calling code stays: the parser reads a Jersey number as a UK
  // one (+44), a Canadian one may read as US (+1).
  const numberCountry = getCountryOfNumber(phone);
  React.useEffect(() => {
    if (numberCountry) {
      setCountry(current =>
        getDialCode(current) === getDialCode(numberCountry)
          ? current
          : numberCountry
      );
    }
  }, [numberCountry]);

  const buttonLabel = t("Country code: {country} {dialCode} – change", {
    ns: "OrganisationProfile",
    country: countryName,
    dialCode: getDialCode(country),
  });
  const inputValue = getPhoneInputValue(phone, country);
  const labelShrunk = focused || inputValue !== "" || draft !== null;
  const hasError = Boolean(touched && error);

  return (
    <>
      <MuiTextField
        fullWidth
        label={label}
        // The focus placeholder; MUI shows it only while the label is lifted.
        placeholder="30 1234567"
        disabled={disabled}
        value={inputValue}
        error={hasError}
        helperText={touched && error ? error : undefined}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          // An incomplete international entry is not kept beyond the field.
          setDraft(null);
          void helpers.setTouched(true);
        }}
        onChange={event => {
          void helpers.setValue(event.target.value);
        }}
        slotProps={{
          // The label stays in the field until it is focused or filled, as in
          // a field without a start adornment.
          inputLabel: {
            shrink: labelShrunk,
          },
          htmlInput: {
            country,
            name,
            type: "tel",
            draft,
            onInternationalEntry: (raw: string | null) => {
              const parsed = raw === null ? null : parseInternationalEntry(raw);
              if (parsed) {
                setCountry(parsed.country);
                void helpers.setValue(parsed.e164);
              } else if (raw !== null) {
                // An incomplete entry replaces the number, so Continue never
                // saves one the field no longer shows.
                void helpers.setValue("");
              }
              setDraft(parsed ? null : raw);
            },
          },
          input: {
            inputComponent: NationalNumberInput,
            startAdornment: (
              <>
                {/* The segment's own lifted label, so the row reads like the
                  other filled fields. Visual only: the button's name says it.
                  A short word ("Vorwahl"), since "Ländervorwahl" fills the segment. */}
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    // Drawn exactly like MUI's lifted label of the number (16px
                    // in a 1.4375em line, moved and scaled to 75 %), so both
                    // labels share one baseline.
                    position: "absolute",
                    top: 0,
                    left: 0,
                    transform: "translate(12px, 4px) scale(0.75)",
                    transformOrigin: "top left",
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                    fontSize: 16,
                    lineHeight: "1.4375em",
                    letterSpacing: "inherit",
                    color: disabled
                      ? "text.disabled"
                      : hasError
                        ? LABEL_ERROR_COLOR
                        : focused
                          ? LABEL_FOCUSED_COLOR
                          : LABEL_COLOR,
                  }}
                >
                  {t("Dial code", { ns: "OrganisationProfile" })}
                </Box>
                <Tooltip title={buttonLabel}>
                  <ButtonBase
                    aria-label={buttonLabel}
                    aria-haspopup="listbox"
                    aria-expanded={Boolean(menuAnchor)}
                    disabled={disabled}
                    onClick={event => setMenuAnchor(event.currentTarget)}
                    sx={{
                      alignSelf: "stretch",
                      width: SEGMENT_WIDTH,
                      flex: "none",
                      pl: "12px",
                      pr: "8px",
                      justifyContent: "flex-start",
                      // The dial code always sits on the number's value line,
                      // below its own lifted label, whatever the number's label
                      // does.
                      alignItems: "flex-start",
                      pt: `${VALUE_TOP}px`,
                      borderRight: `1px solid ${DIVIDER_COLOR}`,
                      borderRadius: "4px 0 0 0",
                      fontFamily: "inherit",
                      "&:hover": { bgcolor: "rgba(0, 30, 100, 0.06)" },
                    }}
                  >
                    {/* The input's own value box: same height, same text, the
                        flag centred on the line. */}
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        ...FIELD_VALUE_TEXT_SX,
                        height: VALUE_LINE_HEIGHT,
                      }}
                    >
                      <OnboardingFlag country={country} name={countryName} />
                      <span>{getDialCode(country)}</span>
                    </Box>
                    {/* The same arrow as the selects (MUI's select icon: 24px,
                        7px from the edge, centred in the field). */}
                    <ArrowDropDownIcon
                      aria-hidden
                      sx={{
                        position: "absolute",
                        right: 4,
                        top: "calc(50% - 11.5px)",
                        fontSize: 24,
                        color: "#1A4CBF",
                      }}
                    />
                  </ButtonBase>
                </Tooltip>
              </>
            ),
          },
        }}
        sx={{
          // The segment starts at the field's edge (MUI pads an adorned field).
          "& .MuiFilledInput-root": { alignItems: "stretch", pl: 0 },
          // The number starts where its label does (MUI clears the padding
          // of an adorned input with a more specific rule, hence `&&`).
          "&& .MuiFilledInput-input": {
            pl: `${LABEL_LEFT - SEGMENT_WIDTH}px`,
          },
          "& .MuiInputLabel-root": {
            // The same heights as the other fields' labels (postcode, city).
            transform: `translate(${LABEL_LEFT}px, 13px) scale(1)`,
            "&.MuiInputLabel-shrink": {
              transform: `translate(${LABEL_LEFT}px, 4px) scale(0.75)`,
            },
          },
        }}
      />
      <OnboardingCountryCodeMenu
        anchorEl={menuAnchor}
        selected={country}
        onClose={() => setMenuAnchor(null)}
        onSelect={next => {
          setCountry(next);
          setMenuAnchor(null);
          void helpers.setValue(switchPhoneCountry(phone, next));
        }}
      />
    </>
  );
};
